import { useEffect, useState } from 'react';
import { Download, Eye, FileArchive, ImageOff, Library, Video } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

import { companyFileContentUrl, companyFilePreviewUrl, companyFileThumbnailUrl } from './api';
import { bytesToHuman } from './format';
import type { CompanyFileDto } from './types';

type PreviewKind = 'image' | 'video-file' | 'none';

const previewableImageMimes = new Set(['image/png', 'image/jpeg', 'image/gif', 'image/webp']);

const getCompanyFilePreviewKind = (
  file: Pick<CompanyFileDto, 'display_name' | 'detected_mime_type'>,
): PreviewKind => {
  const mime = (file.detected_mime_type ?? '').toLowerCase();

  if (previewableImageMimes.has(mime)) return 'image';
  if (mime.startsWith('video/')) return 'video-file';
  return 'none';
};

export const CompanyFileThumbnail = ({
  file,
  className,
  onPreview,
}: {
  file: CompanyFileDto;
  className?: string;
  onPreview?: () => void;
}) => {
  const { t } = useTranslation('fileLibrary');
  const [broken, setBroken] = useState(false);
  const kind = getCompanyFilePreviewKind(file);

  useEffect(() => {
    setBroken(false);
  }, [file.company_file_id]);

  const body =
    kind === 'image' && !broken ? (
      <img
        src={companyFileThumbnailUrl(file.company_file_id)}
        alt={file.display_name}
        loading="lazy"
        onError={() => setBroken(true)}
        className="h-full w-full object-cover"
      />
    ) : (
      <span className="flex h-full w-full items-center justify-center bg-efs-bg-subtle text-efs-text-muted">
        {kind === 'video-file' ? (
          <Video className="h-5 w-5" aria-hidden="true" />
        ) : broken ? (
          <ImageOff className="h-5 w-5" aria-hidden="true" />
        ) : (
          <FileArchive className="h-5 w-5" aria-hidden="true" />
        )}
      </span>
    );

  if (!onPreview || kind === 'none') {
    return (
      <span
        className={cn(
          'block h-14 w-14 shrink-0 overflow-hidden rounded-md border border-efs-border',
          className,
        )}
        aria-label={t('efs.file_library.preview.thumbnail_label', { name: file.display_name })}
      >
        {body}
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        onPreview();
      }}
      className={cn(
        'block h-14 w-14 shrink-0 overflow-hidden rounded-md border border-efs-border focus:outline-none focus:ring-2 focus:ring-efs-accent',
        className,
      )}
      aria-label={t('efs.file_library.preview.open_for_file', { name: file.display_name })}
    >
      {body}
    </button>
  );
};

const FileMetadata = ({ file }: { file: CompanyFileDto }) => {
  const { t } = useTranslation('fileLibrary');
  return (
    <dl className="grid gap-2 rounded-md border border-efs-border bg-efs-bg-subtle p-3 text-sm sm:grid-cols-[8rem_minmax(0,1fr)]">
      <dt className="text-efs-text-muted">{t('efs.file_library.preview.type')}</dt>
      <dd className="min-w-0 break-all">
        {file.detected_mime_type ?? t('efs.file_library.preview.unknown_type')}
      </dd>
      <dt className="text-efs-text-muted">{t('efs.file_library.files.size')}</dt>
      <dd>{bytesToHuman(file.size_bytes)}</dd>
      <dt className="text-efs-text-muted">{t('efs.file_library.preview.sha256')}</dt>
      <dd className="min-w-0 break-all font-mono text-xs">{file.sha256}</dd>
    </dl>
  );
};

export const CompanyFilePreviewDialog = ({
  file,
  open,
  onOpenChange,
}: {
  file: CompanyFileDto | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const { t } = useTranslation('fileLibrary');
  const [failed, setFailed] = useState(false);
  const kind = file ? getCompanyFilePreviewKind(file) : 'none';

  useEffect(() => {
    setFailed(false);
  }, [file?.company_file_id, open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-5xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{file?.display_name ?? t('efs.file_library.preview.title')}</DialogTitle>
          <DialogDescription>{t('efs.file_library.preview.subtitle')}</DialogDescription>
        </DialogHeader>
        {file ? (
          <div className="space-y-4">
            {kind === 'image' && !failed ? (
              <img
                src={companyFilePreviewUrl(file.company_file_id)}
                alt={file.display_name}
                onError={() => setFailed(true)}
                className="max-h-[65vh] w-full rounded-md border border-efs-border object-contain"
              />
            ) : (
              <div className="flex min-h-48 flex-col items-center justify-center rounded-md border border-dashed border-efs-border bg-efs-bg-subtle p-6 text-center text-sm text-efs-text-muted">
                <Library className="mb-3 h-8 w-8" aria-hidden="true" />
                <div className="font-medium text-efs-text-primary">
                  {failed
                    ? t('efs.file_library.preview.failed')
                    : t('efs.file_library.preview.not_available')}
                </div>
                <div className="mt-1">{t('efs.file_library.preview.download_hint')}</div>
              </div>
            )}
            <FileMetadata file={file} />
            <div className="flex justify-end">
              <Button asChild variant="outline">
                <a
                  href={companyFileContentUrl(file.company_file_id)}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Download className="h-4 w-4" />
                  {t('efs.file_library.actions.download')}
                </a>
              </Button>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export const PreviewButton = ({
  file,
  onPreview,
  compact = false,
  hoverOpen: controlledHoverOpen,
  onHoverOpenChange,
}: {
  file: CompanyFileDto;
  onPreview: () => void;
  compact?: boolean;
  hoverOpen?: boolean;
  onHoverOpenChange?: (open: boolean) => void;
}) => {
  const { t } = useTranslation('fileLibrary');
  const [uncontrolledHoverOpen, setUncontrolledHoverOpen] = useState(false);
  const [hoverFailed, setHoverFailed] = useState(false);
  const kind = getCompanyFilePreviewKind(file);
  const canHoverPreview = kind === 'image';
  const hoverOpen = controlledHoverOpen ?? uncontrolledHoverOpen;

  const setHoverOpen = (open: boolean) => {
    if (controlledHoverOpen === undefined) setUncontrolledHoverOpen(open);
    onHoverOpenChange?.(open);
  };

  useEffect(() => {
    setUncontrolledHoverOpen(false);
    setHoverFailed(false);
  }, [file.company_file_id]);

  const showHoverPreview = () => {
    if (canHoverPreview) setHoverOpen(true);
  };

  const hideHoverPreview = () => setHoverOpen(false);

  return (
    <Popover open={hoverOpen && canHoverPreview} onOpenChange={setHoverOpen}>
      <PopoverAnchor asChild>
        <Button
          type="button"
          variant="ghost"
          size={compact ? 'icon' : 'sm'}
          onMouseEnter={showHoverPreview}
          onMouseLeave={hideHoverPreview}
          onFocus={showHoverPreview}
          onBlur={hideHoverPreview}
          onKeyDown={(event) => {
            if (event.key === 'Escape' && hoverOpen) {
              event.preventDefault();
              event.stopPropagation();
              hideHoverPreview();
            }
          }}
          onClick={(event) => {
            event.stopPropagation();
            hideHoverPreview();
            onPreview();
          }}
          aria-label={t('efs.file_library.preview.open_for_file', { name: file.display_name })}
        >
          <Eye className={compact ? 'h-4 w-4' : 'mr-1 h-3 w-3'} aria-hidden="true" />
          {compact ? (
            <span className="sr-only">{t('efs.file_library.actions.preview')}</span>
          ) : (
            t('efs.file_library.actions.preview')
          )}
        </Button>
      </PopoverAnchor>
      <PopoverContent
        side="top"
        align="center"
        sideOffset={8}
        onOpenAutoFocus={(event) => event.preventDefault()}
        onCloseAutoFocus={(event) => event.preventDefault()}
        onEscapeKeyDown={(event) => {
          event.preventDefault();
          hideHoverPreview();
        }}
        className="pointer-events-none relative h-64 w-64 overflow-hidden p-2"
        data-testid="file-hover-preview"
        aria-hidden="true"
      >
        {hoverFailed ? (
          <div className="flex h-full w-full flex-col items-center justify-center rounded border border-dashed border-efs-border bg-efs-bg-subtle p-4 text-center text-xs text-efs-text-muted">
            <ImageOff className="mb-2 h-6 w-6" aria-hidden="true" />
            {t('efs.file_library.preview.failed')}
          </div>
        ) : (
          <img
            src={companyFileThumbnailUrl(file.company_file_id)}
            alt=""
            onError={() => setHoverFailed(true)}
            className="h-full w-full rounded border border-efs-border bg-efs-bg-subtle object-contain"
          />
        )}
        <div className="absolute bottom-2 left-2 right-2 truncate rounded-b bg-black/70 px-2 py-1 text-xs font-medium text-white">
          {file.display_name}
        </div>
      </PopoverContent>
    </Popover>
  );
};
